import * as React from 'react';
// @ts-expect-error
import {components} from 'react-select';
// @ts-expect-error
import type {OptionProps, SingleValueProps} from 'react-select';

import {useQuery} from '@apollo/react-hooks';

import {makeDataDangerous} from 'nutshell-core/utils/graphql-errors';

import {colors} from 'shells/colors/colors';
import {OptionWithIcon} from 'shells/select-option-components/option-with-icon';
import type {ListOptionWithIcon} from 'shells/select-option-components/option-with-icon';

import type {
    GetUsers as GetUsersQuery,
    GetUsersVariables as GetUsersQueryVariables,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {useGetSessionUser} from '../use-get-session-user';

import {GraphQLSelect} from './graphql-select';
import GET_USERS from './queries/get-users.graphql';

// For consistency, max height should be managed by the component.
// Multi-value selects should have a large max height to fit many selections.
const VALUE_CONTAINER_PADDING = 8;
const SINGLE_VALUE_CONTAINER_MAX_HEIGHT = 73;
const MULTI_VALUE_CONTAINER_MAX_HEIGHT = 300;

export type FilterOption = {
    label: string;
    value: string;
    data: {
        label: string;
        value: string;
    };
};

type Props = {
    inputId?: string;
    selectedId: string | null | undefined;
    selectedIds?: string[] | null | undefined;
    // Behavior of onChange is dependent on isMulti
    // If isMulti is true, value is ListOptionWithIcon[], otherwise it's ListOptionWithIcon
    onChange: (value?: any) => void;
    onClearValue?: () => void;
    labelKey?: string;
    valueKey?: string;
    autoFocus?: boolean;
    autoBlur?: boolean;
    isLoading?: boolean;
    noResultsText?: string;
    styles?: any;
    staticOptions?: ListOptionWithIcon[];
    isMulti?: boolean;
    includeUnprovisioned?: boolean;
    shouldShowLoggedInUserFirst?: boolean;
    filterOption?: (option: FilterOption, inputValue: string) => boolean;
};

export const GraphQLSelectUsers = (props: Props) => {
    const {isMulti = true} = props;
    const {user: loggedInUser} = useGetSessionUser();
    const {data, loading, fetchMore, error} = useQuery<GetUsersQuery, GetUsersQueryVariables>(
        GET_USERS,
        {
            notifyOnNetworkStatusChange: true,
            variables: {
                includeUnprovisioned: props.includeUnprovisioned,
            },
        }
    );

    const {users} = makeDataDangerous(data);

    const options: ListOptionWithIcon[] = users
        ? // @ts-expect-error
          users.edges.map((user) => ({
              value: user.node.id,
              label: user.node.name + (user.node.isLoggedInUser ? ' (You)' : ''),
              avatarUrl: user.node.avatarUrl,
              initials: user.node.initials,
              isLoggedInUser: loggedInUser ? user.node.id === loggedInUser.id : false,
          }))
        : [];

    const sortedOptions = props.shouldShowLoggedInUserFirst
        ? options.sort((a, b) => {
              if (a.isLoggedInUser) return -1;
              if (b.isLoggedInUser) return 1;

              return 0;
          })
        : options;

    const userOptions: ListOptionWithIcon[] = props.staticOptions
        ? props.staticOptions.concat(sortedOptions)
        : options;

    const hasNextPage = users ? users.pageInfo.hasNextPage : undefined;
    const valueContainerMaxHeight = isMulti
        ? MULTI_VALUE_CONTAINER_MAX_HEIGHT
        : SINGLE_VALUE_CONTAINER_MAX_HEIGHT;
    const controlMaxHeight = valueContainerMaxHeight + VALUE_CONTAINER_PADDING;

    const fetchMoreUsers = () =>
        fetchMore({
            query: GET_USERS,
            variables: {
                after: users.pageInfo.endCursor,
                // this should be included in the cursor :|
                includeUnprovisioned: props.includeUnprovisioned,
            },
            updateQuery: (previousResult, {fetchMoreResult}) => {
                if (!fetchMoreResult || !previousResult) return;

                return {
                    users: {
                        __typename: 'UsersConnection',
                        // @ts-expect-error
                        pageInfo: fetchMoreResult.users.pageInfo,
                        // @ts-expect-error
                        edges: [...previousResult.users.edges, ...fetchMoreResult.users.edges],
                    },
                };
            },
        });

    // Automatically fetch more users if there are more than what's showing
    React.useEffect(() => {
        if (users && hasNextPage) {
            fetchMoreUsers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, hasNextPage]);

    const componentStyles = {
        // @ts-expect-error
        control: (base) => ({
            ...base,
            height: 'auto',
            maxHeight: `${controlMaxHeight}px`,
            overflow: 'none',
        }),
        // @ts-expect-error
        valueContainer: (base) => ({
            ...base,
            padding: isMulti ? `${VALUE_CONTAINER_PADDING}` : `0 ${VALUE_CONTAINER_PADDING}px`,
            // This fits in the container (see above) and accounts for the border
            maxHeight: `${valueContainerMaxHeight}px`,
            overflow: 'scroll',
            '::-webkit-scrollbar': {
                display: 'none',
            },
        }),
        // @ts-expect-error
        multiValue: (base) => ({
            ...base,
            backgroundColor: colors.orangeDk,
            color: '#fff',
            transition: 'color 200ms linear',
        }),
        // @ts-expect-error
        multiValueLabel: (base) => ({
            ...base,
            color: '#fff',
            transition: 'color 200ms linear',
            fontSize: '15px',
        }),
        // @ts-expect-error
        multiValueRemove: (base) => ({
            ...base,
            '&:hover': {
                backgroundColor: colors.orangeDk,
                color: colors.offWhiteDk,
            },
            fontSize: '15px',
        }),
    } as const;

    return (
        <GraphQLSelect
            {...props}
            selectedIds={props.selectedIds}
            valueKey='value'
            labelKey='label'
            onChange={(selection) => {
                props.onChange(selection);
            }}
            onClearValue={props.onClearValue}
            placeholder='Search users…'
            isLoading={loading}
            isMulti={isMulti}
            styles={componentStyles}
            // @ts-expect-error
            components={{
                Option: OptionComponent,
                SingleValue: isMulti ? null : SingleValueComponent, // This ensures that the SingleValue component is only used in single-select mode (we do not want the avatars in multi mode)
            }}
            models={userOptions}
            hasError={Boolean(error)}
            footerButton={
                loggedInUser && loggedInUser.permissions.canAccessSetup
                    ? {
                          label: 'Manage users',
                          onClick: () => {
                              appHistory.push('/setup/user');
                          },
                      }
                    : undefined
            }
        />
    );
};

const OptionComponent = (props: OptionProps) => {
    // This is a hack to get around limitations in our old version of flow
    const spreadableProps: OptionProps = props as any;

    return (
        <components.Option {...spreadableProps}>
            <OptionWithIcon
                label={props.data.label}
                icon={props.data.icon}
                avatarUrl={props.data.avatarUrl}
                initials={props.data.initials}
                isLoggedInUser={props.data.isLoggedInUser}
            />
        </components.Option>
    );
};

const SingleValueComponent = (props: SingleValueProps) => {
    return (
        <components.SingleValue {...props}>
            <OptionWithIcon
                label={props.data.label}
                icon={props.data.icon}
                avatarUrl={props.data.avatarUrl}
                initials={props.data.initials}
                isLoggedInUser={props.data.isLoggedInUser}
            />
        </components.SingleValue>
    );
};
